import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../api";
import { isEditLocked } from "../utils/isEditLocked";

import PreferencesForm from "../components/PreferencesForm";
import PreferencesResults from "../components/PreferencesResults";

import "../styles/Preferences.css";
import "../styles/buttons.css";

const dateKey = (v) => {
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; // already YYYY-MM-DD
    const d = new Date(v);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
    return String(v);
  }
  const d = new Date(v);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return String(v);
};

export default function Preferences({
  mock = false,
  user_id: injectedId,
  allowNext = true,
  frameId,
}) {
  /* routing / ids */
  const { user_id: urlId } = useParams();
  const user_id = injectedId || urlId;
  const location = useLocation();
  const navigate = useNavigate();
  const forecastRef = useRef(null);

  /* account state */
  const [accountStatus, setAccountStatus] = useState(null);
  const locked = isEditLocked(accountStatus);
  const trial = accountStatus?.subscription?.plan === "trial";
  const isEssentials = accountStatus?.subscription?.plan === "essentials";
  const plan  = accountStatus?.subscription?.plan;

  useEffect(() => {
    api
      .get("/account_status/", { params: { user_id } })
      .then((r) => {
        if (r?.data?.status === "success")
          setAccountStatus(r.data.account_status);
      })
      .catch(() => {});
  }, [user_id]);

  /* venue list passed in via location.state */
  const selectedVenues = useMemo(
    () => (location.state && location.state.selectedVenues) || [],
    [location.state]
  );

  /* form / result state */
  const today = new Date().toISOString().split("T")[0];
  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 30);
  const defaultEndStr = defaultEndDate.toISOString().split("T")[0];

  const [preferences, setPreferences] = useState({
    temperature: "",
    humidity: "",
    sunClouds: "",
    sunset: "",
    wind: "",
    precipitation: "",
    startDate: today,
    endDate: defaultEndStr,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [forecastResults, setForecastResults] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState({});
  const [apiError, setApiError] = useState(null);

  const totalSelected = useMemo(
    () => Object.values(selectedMatches).reduce((n, arr) => n + arr.length, 0),
    [selectedMatches]
  );

  /* submit handler – makes API call */
  const handleSeeForecast = async () => {
    const { temperature, humidity, sunClouds, wind, precipitation, sunset } =
      preferences;
    if (
      ![temperature, humidity, sunClouds, wind, precipitation, sunset].some(
        Boolean
      )
    ) {
      setApiError("Please select at least one weather preference.");
      return;
    }

    setIsLoading(true);
    setApiError(null);

    /* ───────── MOCK FLOW ───────── */
    if (mock) {
      const fixedVenue = "Nakoma Resort";
      const fixedStart = "2025-06-15";
      const fixedEnd = "2025-07-15";

      setPreferences((prev) => ({
        ...prev,
        startDate: fixedStart,
        endDate: fixedEnd,
      }));

      const fakeMatches = [
        { d: "2025-06-15", s: 90 },
        { d: "2025-06-16", s: 66 },
        { d: "2025-06-17", s: 52 },
        { d: "2025-06-18", s: 93 },
        { d: "2025-06-19", s: 69 },
        { d: "2025-06-20", s: 55 },
        { d: "2025-06-21", s: 90 },
        { d: "2025-06-22", s: 72 },
        { d: "2025-06-23", s: 51 },
        { d: "2025-06-24", s: 93 },
        { d: "2025-06-25", s: 65 },
        { d: "2025-06-26", s: 54 },
        { d: "2025-06-27", s: 90 },
        { d: "2025-06-28", s: 68 },
        { d: "2025-06-29", s: 50 },
        { d: "2025-06-30", s: 93 },
        { d: "2025-07-01", s: 71 },
        { d: "2025-07-02", s: 53 },
        { d: "2025-07-03", s: 90 },
        { d: "2025-07-04", s: 74 },
        { d: "2025-07-05", s: 56 },
        { d: "2025-07-06", s: 93 },
        { d: "2025-07-07", s: 67 },
        { d: "2025-07-08", s: 52 },
        { d: "2025-07-09", s: 90 },
        { d: "2025-07-10", s: 70 },
        { d: "2025-07-11", s: 55 },
        { d: "2025-07-12", s: 93 },
        { d: "2025-07-13", s: 73 },
        { d: "2025-07-14", s: 51 },
        { d: "2025-07-15", s: 90 },
      ];

      const fakeMatchesWithExtras = fakeMatches.map(({ d, s }) => ({
        forecast_date: d, // "YYYY-MM-DD"
        overall_score: s,
        temperature_score: s,
        humidity_score: s,
        sunClouds_score: s,
        wind_score: s,
        precipitation_score: s,
        sunset_score: s,
        temp_f: 72,
        humidity_raw: 50,
        sunlight_raw: 220,
        wind_raw: 4,
        precip_raw: 0,
        sunset_raw: "8:05 PM",
        temperature: 72,
        windSpeed: 4,
        precip_mm: 0,
        cloud: 25,
      }));

      setTimeout(() => {
        setForecastResults([
          {
            venue: { name: fixedVenue, date: fixedStart },
            matches: fakeMatchesWithExtras,
          },
        ]);
        setIsLoading(false);
      }, 400);
      return;
    }

    /* ───────── REAL API FLOW ───────── */
    try {
      const params = {
        user_id,
        start_time: `${preferences.startDate} 00:00:00`,
        end_time: `${preferences.endDate} 23:59:59`,
        temperature: preferences.temperature,
        humidity: preferences.humidity,
        sunClouds: preferences.sunClouds,
        wind: preferences.wind,
        precipitation: preferences.precipitation,
        sunset_param: preferences.sunset,
        venues: selectedVenues, // array of venue IDs/names
        scale: "daily",
      };
      const { data } = await api.get("/get_multiple_forecast_matches/", {
        params,
      });
      if (data.status === "success") {
        setForecastResults(data.results);
      } else {
        setApiError(data.message || "Error fetching forecast");
      }
    } catch (e) {
      setApiError("Error fetching forecast. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /* date-picker handler (normalize to avoid type mismatches) */
  const handleSelectDate = useCallback(
    (venueName, match) => {
      const key = dateKey(match.forecast_date);
      setSelectedMatches((prev) => {
        if (isEssentials) {
          // Determine if clicking the same date again (toggle off)
          const currentlySelected = Object.values(prev)
            .flat()
            .map((m) => dateKey(m.forecast_date));
          const sameAsExisting =
            currentlySelected.length === 1 && currentlySelected[0] === key;

          if (sameAsExisting) {
            // Toggle off → clear all
            return {};
          }
          // Replace any previous selection with exactly this one
          return { [venueName]: [{ ...match, forecast_date: key }] };
        }

        // Default behavior (non-Essentials): multi-select per venue
        const list = prev[venueName] || [];
        const exists = list.some((m) => dateKey(m.forecast_date) === key);
        const updated = exists
          ? list.filter((m) => dateKey(m.forecast_date) !== key)
          : [...list, { ...match, forecast_date: key }];
        return { ...prev, [venueName]: updated };
      });
    },
    [isEssentials]
  );

  /* navigation */
  const handleContinue = async () => {
    // Build payload expected by /store_user_preferences/ → { name, date }
    const selections = Object.entries(selectedMatches).flatMap(
      ([venueName, matches]) =>
        matches.map((m) => ({
          name: venueName,
          date: dateKey(m.forecast_date), // "YYYY-MM-DD"
        }))
    );

    if (!selections.length) {
      setApiError("Please select at least one date.");
      return;
    }

    try {
      // 1) Persist selections server-side so Dashboard sees them immediately
      const { data } = await api.post("/store_user_preferences/", {
        user_id,
        selected_venues: selections,
      });

      if (data?.status !== "success") {
        setApiError(
          data?.user_message ||
            data?.message ||
            "Could not save your selections. Please try again."
        );
        return;
      }

      sessionStorage.setItem(
        "bd2m:selectedMatches",
        JSON.stringify(selectedMatches)
      );
      sessionStorage.setItem("bd2m:preferences", JSON.stringify(preferences));
      sessionStorage.setItem(
        "bd2m:selectedVenues",
        JSON.stringify(selectedVenues)
      );
      if (forecastResults) {
        sessionStorage.setItem(
          "bd2m:forecastResults",
          JSON.stringify(forecastResults)
        );
      }

      // 4) Navigate
      navigate(`/dashboard/${user_id}`, { replace: true });
    } catch (e) {
      setApiError("Could not save your selections. Please try again.");
    }
  };

  /* helpers */
  const onFormChange = (key, value) =>
    setPreferences((p) => ({ ...p, [key]: value }));
  const showForm = !forecastResults && !isLoading;
  const showResults = !!forecastResults;

  return (
    <div className="preferences-page-main-container" id={frameId}>
      <header className="logo">
        <Link to={`/dashboard/${user_id}`}>BestDay2Marry</Link>
      </header>

      {showForm && (
        <PreferencesForm
          trial={trial}
          today={today}
          defaultEnd={defaultEndStr}
          preferences={preferences}
          onChange={onFormChange}
          onSubmit={handleSeeForecast}
        />
      )}

      {isLoading && (
        <div className="fetching-matches-container">
          <h1 className="fetching-title">
            Fetching Dates That Match Your Preferences…
          </h1>
          <div className="spinner" />
        </div>
      )}

      {showResults && (
        <PreferencesResults
          ref={forecastRef}
          forecastResults={forecastResults}
          preferences={preferences}
          selectedMatches={selectedMatches}
          onSelectDate={handleSelectDate}
          onBack={() => setForecastResults(null)}
          nextDisabled={locked || totalSelected === 0}
          plan={plan}
          onContinue={handleContinue}
          apiError={apiError}
        />
      )}
    </div>
  );
}
